import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const TEMPLATES = [
  { id: 'banko_ind', name: 'Banko indtægt', desc: 'Banko', debit: '6620', credit: '1020', icon: 'Kasse' },
  { id: 'banko_ud', name: 'Banko udgift', desc: 'Banko udgift', debit: '2620', credit: '6620', icon: 'Kasse' },
  { id: 'kontingent_bank', name: 'Kontingent (bank)', desc: 'Kontingent', debit: '6610', credit: '1010', icon: 'Bank' },
  { id: 'kontingent_kasse', name: 'Kontingent (kasse)', desc: 'Kontingent', debit: '6620', credit: '1010', icon: 'Kasse' },
  { id: 'arr_ind_kasse', name: 'Arrangement indtægt', desc: 'Arrangement', debit: '6620', credit: '1030', icon: 'Kasse' },
  { id: 'arr_ud_kasse', name: 'Arrangement udgift', desc: 'Arrangement udgift', debit: '2610', credit: '6620', icon: 'Kasse' },
  { id: 'arr_ind_bank', name: 'Arrangement indtægt (bank)', desc: 'Arrangement', debit: '6610', credit: '1030', icon: 'Bank' },
  { id: 'arr_ud_bank', name: 'Arrangement udgift (bank)', desc: 'Arrangement udgift', debit: '2610', credit: '6610', icon: 'Bank' },
  { id: 'foredrag_ind', name: 'Foredrag indtægt', desc: 'Foredrag', debit: '6620', credit: '1040', icon: 'Kasse' },
  { id: 'foredrag_ud', name: 'Foredrag udgift', desc: 'Foredrag udgift', debit: '2630', credit: '6620', icon: 'Kasse' },
  { id: 'bankgebyr', name: 'Bankgebyr', desc: 'Bankgebyr', debit: '2430', credit: '6610', icon: 'Bank' },
  { id: 'mobilgebyr', name: 'Mobilgebyr', desc: 'Mobilgebyr', debit: '2420', credit: '6610', icon: 'Bank' },
  { id: 'forsikring', name: 'Forsikring', desc: 'Forsikring', debit: '2510', credit: '6610', icon: 'Bank' },
  { id: 'indsat_bank', name: 'Indsat i bank', desc: 'Indsat i bank', debit: '6610', credit: '6620', icon: 'Overf.' },
  { id: 'haevet_bank', name: 'Hævet fra bank', desc: 'Hævet fra bank', debit: '6620', credit: '6610', icon: 'Overf.' },
  { id: 'gave', name: 'Gaver og blomster', desc: 'Gaver/blomster', debit: '2640', credit: '6620', icon: 'Kasse' },
  { id: 'forplejning', name: 'Forplejning', desc: 'Forplejning', debit: '2330', credit: '6620', icon: 'Kasse' },
  { id: 'diverse_ind', name: 'Diverse indtægt', desc: 'Diverse', debit: '6620', credit: '1310', icon: 'Kasse' },
  { id: 'diverse_ud', name: 'Diverse udgift', desc: 'Diverse udgift', debit: '2900', credit: '6620', icon: 'Kasse' },
];

export default function QuickEntry() {
  const { apiFetch } = useAuth();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [debitAccount, setDebitAccount] = useState('');
  const [creditAccount, setCreditAccount] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filter, setFilter] = useState('');

  useEffect(() => {
    apiFetch('/api/accounts').then(setAccounts).catch(() => {});
  }, [apiFetch]);

  const findAccountId = (accountNumber) => {
    const acc = accounts.find(a => a.account_number === accountNumber);
    return acc ? acc.id : null;
  };

  const selectTemplate = (t) => {
    setSelectedTemplate(t);
    setDescription(t.desc);
    setDebitAccount(t.debit);
    setCreditAccount(t.credit);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setError('Angiv et beløb.'); return; }

    const debitId = findAccountId(debitAccount);
    const creditId = findAccountId(creditAccount);
    if (!debitId || !creditId) { setError('Konti kunne ikke findes. Tjek kontoplan.'); return; }

    setSaving(true);
    try {
      const result = await apiFetch('/api/vouchers', {
        method: 'POST',
        body: {
          date,
          description: description || 'Bogføring',
          lines: [
            { account_id: debitId, debit: amt, credit: 0, description: '' },
            { account_id: creditId, debit: 0, credit: amt, description: '' }
          ]
        }
      });
      setSuccess(`Bilag #${result.voucher_number} oprettet!`);
      setAmount('');
      setSaving(false);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const filteredTemplates = filter
    ? TEMPLATES.filter(t => t.name.toLowerCase().includes(filter.toLowerCase()))
    : TEMPLATES;

  const getAccountName = (accNum) => {
    const acc = accounts.find(a => a.account_number === accNum);
    return acc ? `${acc.account_number} ${acc.name}` : accNum;
  };

  return (
    <div>
      <div className="page-header">
        <h1>Hurtig bogføring</h1>
        <button className="btn btn-secondary" onClick={() => navigate('/app/bilag/ny')}>
          Avanceret bilag
        </button>
      </div>

      <div className="quick-entry-layout">
        <div className="card quick-templates">
          <h3>Vælg type</h3>
          <input className="form-control" placeholder="Søg skabelon..."
            value={filter} onChange={e => setFilter(e.target.value)}
            style={{marginBottom: '0.75rem'}} />
          <div className="template-grid">
            {filteredTemplates.map(t => (
              <button key={t.id}
                className={`template-btn ${selectedTemplate?.id === t.id ? 'active' : ''}`}
                onClick={() => selectTemplate(t)}>
                <span className="template-icon">{t.icon}</span>
                <span className="template-name">{t.name}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="card quick-form">
          <h3>Bogfør</h3>
          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Dato</label>
              <input type="date" className="form-control" value={date}
                onChange={e => setDate(e.target.value)} required />
            </div>

            <div className="form-group">
              <label>Beskrivelse</label>
              <input className="form-control" value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Hvad er det for?" required />
            </div>

            <div className="form-group">
              <label>Beløb (kr.)</label>
              <input type="number" className="form-control amount-input" value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="0,00" step="0.01" min="0.01" required autoFocus />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Debet (fra)</label>
                <select className="form-control" value={debitAccount}
                  onChange={e => setDebitAccount(e.target.value)} required>
                  <option value="">Vælg konto...</option>
                  {accounts.map(a => (
                    <option key={a.id} value={a.account_number}>
                      {a.account_number} - {a.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Kredit (til)</label>
                <select className="form-control" value={creditAccount}
                  onChange={e => setCreditAccount(e.target.value)} required>
                  <option value="">Vælg konto...</option>
                  {accounts.map(a => (
                    <option key={a.id} value={a.account_number}>
                      {a.account_number} - {a.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {debitAccount && creditAccount && (
              <div className="quick-preview">
                <div className="preview-line">
                  <span>Debet:</span> <strong>{getAccountName(debitAccount)}</strong>
                </div>
                <div className="preview-line">
                  <span>Kredit:</span> <strong>{getAccountName(creditAccount)}</strong>
                </div>
              </div>
            )}

            <button type="submit" className="btn btn-primary" style={{width: '100%', justifyContent: 'center', marginTop: '1rem'}}
              disabled={saving || !amount || !debitAccount || !creditAccount}>
              {saving ? 'Gemmer...' : 'Bogf\u00f8r'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
